import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { aud, fmtDate } from "@/lib/format";
import { WeatherCard } from "@/components/common/WeatherCard";
import { DrawingAlertsBanner } from "@/components/field/DrawingAlertsBanner";
import { AuthImage } from "@/components/common/AuthImage";
import { ContractorDefectsCard } from "@/components/defects/ContractorDefectsCard";
import { ContractorSubmittalsCard } from "@/components/submittals/ContractorSubmittalsCard";
import {
  IssueItemsEditor,
  newEditorItem,
  editorItemsToPayload,
} from "@/components/field/IssueItemsEditor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  FileQuestion,
  FolderKanban,
  Hammer,
  Megaphone,
  PackageOpen,
  Receipt,
  ScrollText,
  Truck,
} from "lucide-react";

const SectionCard = ({ icon: Icon, title, count, children, testId }) => (
  <Card className="shadow-sm" data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-primary" /> {title}
        {count > 0 && (
          <Badge variant="outline" className="border-primary/40 bg-accent text-primary">
            {count}
          </Badge>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">{children}</CardContent>
  </Card>
);

const EmptyNote = ({ children, testId }) => (
  <p className="py-3 text-sm text-muted-foreground" data-testid={testId}>
    {children}
  </p>
);

const StatusBadge = ({ status }) => {
  if (status === "ready_for_review")
    return (
      <Badge variant="outline" className="border-sky-500/40 bg-sky-500/10 text-sky-400">
        Ready for review
      </Badge>
    );
  if (status === "resolved")
    return (
      <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-400">
        Resolved
      </Badge>
    );
  return (
    <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-400">
      Open
    </Badge>
  );
};

const IssuePhotos = ({ issue }) => {
  const photos = (issue.items || []).flatMap((it, i) =>
    (it.photos || []).map((name) => ({ name, path: `/field/issues/${issue.id}/photos/${name}`, item: i })),
  );
  if (!photos.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {photos.slice(0, 6).map((p, i) => (
        <AuthImage
          key={`${issue.id}-${i}`}
          path={p.path}
          className="h-14 w-14 rounded-lg border border-border"
          testId={`contractor-issue-photo-${issue.id}-${i}`}
        />
      ))}
    </div>
  );
};

export default function ContractorDashboardPage() {
  const { user } = useAuth();
  const viewOnly = user?.portal_access_level === "view"; // Phase 164 — view-only trade logins: look, don't touch
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readying, setReadying] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportJob, setReportJob] = useState("");
  const [reportItems, setReportItems] = useState([newEditorItem()]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/contractor/dashboard");
      setData(res.data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load your dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markReady = async (issue) => {
    setReadying(issue.id);
    try {
      await api.post(`/contractor/issues/${issue.id}/ready`);
      toast.success("Marked as work completed — the builder will review it");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not update the issue"));
    } finally {
      setReadying("");
    }
  };

  const ackPo = async (po) => {
    try {
      await api.post(`/contractor/pos/${po.id}/ack`);
      toast.success(`${po.po_number} acknowledged`);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not acknowledge the PO"));
    }
  };

  const ackRfq = async (r) => {
    try {
      await api.post(`/contractor/rfqs/${r.id}/ack`);
      toast.success(`${r.rfq_number} acknowledged`);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not acknowledge the RFQ"));
    }
  };

  const openReport = () => {
    setReportJob(data?.jobs?.[0]?.id || "");
    setReportItems([newEditorItem()]);
    setReportOpen(true);
  };

  const submitReport = async () => {
    const items = editorItemsToPayload(reportItems);
    if (!reportJob) return toast.error("Pick the job this is about");
    if (!items.length) return toast.error("Describe at least one item");
    setSubmitting(true);
    try {
      await api.post("/contractor/issues", { job_id: reportJob, items });
      toast.success("Issue sent to the builder");
      setReportOpen(false);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not send the issue"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="contractor-dashboard-loading">
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const d = data || {};
  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <div className="space-y-5" data-testid="contractor-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="contractor-dashboard-title">
            G'day, {firstName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Contractor portal — {d.jobs?.length || 0} assigned job{(d.jobs?.length || 0) === 1 ? "" : "s"}
            {viewOnly && <Badge variant="outline" className="ml-2 align-middle text-[10px]" data-testid="contractor-view-only-badge">View only</Badge>}
          </p>
        </div>
        {!viewOnly && (
          <Button onClick={openReport} data-testid="contractor-report-issue-btn">
            <Megaphone className="mr-1.5 h-4 w-4" /> Report an issue
          </Button>
        )}
      </div>

      {/* Phase 142 — re-issued drawings this trade hasn't opened yet (persists until each one is opened) */}
      <DrawingAlertsBanner />

      <WeatherCard />

      {/* Punch-list defects assigned to this trade (Phase 87) */}
      <ContractorDefectsCard />

      {/* Submittals — shop drawings / product data / SWMS lodged for review (Phase 136) */}
      <ContractorSubmittalsCard readOnly={viewOnly} />

      {/* Rectifications assigned to me */}
      {(d.issues_assigned?.length || 0) > 0 && (
        <Card className="border-l-2 border-l-amber-500 shadow-sm" data-testid="contractor-rectifications-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <ClipboardCheck className="h-4 w-4 text-amber-400" /> Rectifications assigned to you
              <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-400">
                {d.issues_assigned.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {d.issues_assigned.map((issue) => (
              <div key={issue.id} className="rounded-xl border border-border bg-background p-3" data-testid={`contractor-rect-${issue.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{issue.job_name}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Raised {fmtDate(issue.created_at)} by {issue.created_by || "the builder"}
                    </p>
                  </div>
                  <StatusBadge status={issue.status} />
                </div>
                <ul className="mt-2 space-y-1.5">
                  {(issue.items || []).map((it, i) => (
                    <li key={i} className="text-sm">
                      <span className="mr-1.5 font-semibold text-primary">{i + 1}.</span>
                      {it.text}
                    </li>
                  ))}
                </ul>
                <IssuePhotos issue={issue} />
                {issue.status === "open" && viewOnly ? (
                  <p className="mt-3 text-xs text-muted-foreground" data-testid={`contractor-mark-ready-locked-${issue.id}`}>View only — the builder can switch you to view &amp; respond to mark work completed.</p>
                ) : issue.status === "open" ? (
                  <Button
                    size="sm"
                    className="mt-3 h-9"
                    disabled={readying === issue.id}
                    onClick={() => markReady(issue)}
                    data-testid={`contractor-mark-ready-btn-${issue.id}`}
                  >
                    <CheckCircle2 className="mr-1.5 h-4 w-4" />
                    {readying === issue.id ? "Updating…" : "Mark work completed"}
                  </Button>
                ) : (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Waiting on the builder to confirm and close this out.
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Schedule + Deliveries */}
      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard icon={CalendarDays} title="My schedule — next 14 days" count={d.schedule?.length || 0} testId="contractor-schedule-card">
          {(d.schedule?.length || 0) === 0 ? (
            <EmptyNote testId="contractor-schedule-empty">Nothing booked in the next fortnight.</EmptyNote>
          ) : (
            <ul className="divide-y divide-border">
              {d.schedule.map((s, i) => (
                <li key={i} className="flex items-center justify-between gap-3 py-2.5" data-testid={`contractor-schedule-row-${i}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{s.title}</p>
                    <p className="text-xs text-muted-foreground">{s.job_name}</p>
                  </div>
                  <p className="shrink-0 text-xs font-semibold text-primary">
                    {fmtDate(s.start_date)}
                    {s.end_date && s.end_date !== s.start_date ? ` → ${fmtDate(s.end_date)}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard icon={Truck} title="Upcoming deliveries" count={d.deliveries?.length || 0} testId="contractor-deliveries-card">
          {(d.deliveries?.length || 0) === 0 ? (
            <EmptyNote testId="contractor-deliveries-empty">No upcoming deliveries on your jobs.</EmptyNote>
          ) : (
            <ul className="divide-y divide-border">
              {d.deliveries.map((del) => (
                <li key={del.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`contractor-delivery-${del.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{del.description || "Delivery"}</p>
                    <p className="text-xs text-muted-foreground">
                      {del.job_name}
                      {del.supplier_name ? ` · ${del.supplier_name}` : ""}
                    </p>
                  </div>
                  <p className="shrink-0 text-xs font-semibold text-primary">{fmtDate(del.expected_date)}</p>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      {/* POs + RFQs (only when linked to a supplier record or data exists) */}
      {(d.pos?.length || d.rfqs?.length || d.linked?.supplier) ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <SectionCard icon={Receipt} title="Purchase orders to you" count={d.pos?.length || 0} testId="contractor-pos-card">
            {(d.pos?.length || 0) === 0 ? (
              <EmptyNote testId="contractor-pos-empty">No purchase orders issued to you yet.</EmptyNote>
            ) : (
              <ul className="divide-y divide-border">
                {d.pos.map((po) => (
                  <li key={po.id} className="py-2.5" data-testid={`contractor-po-${po.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{po.po_number}</p>
                        <p className="text-xs text-muted-foreground">
                          {po.job_name}
                          {po.expected_date ? ` · due ${fmtDate(po.expected_date)}` : ""}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold">{aud(po.total_inc_gst)}</p>
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{po.status}</p>
                      </div>
                    </div>
                    {po.acknowledged ? (
                      <p className="mt-1 flex items-center gap-1 text-xs text-emerald-400" data-testid={`contractor-po-acked-${po.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Acknowledged {fmtDate(po.acknowledged_at)}
                      </p>
                    ) : viewOnly ? (
                      <p className="mt-1 text-xs text-muted-foreground" data-testid={`contractor-po-ack-locked-${po.id}`}>Awaiting acknowledgement · view only</p>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-1.5 h-7 border-primary/40 px-2.5 text-xs text-primary hover:bg-primary/10"
                        onClick={() => ackPo(po)}
                        data-testid={`contractor-po-ack-btn-${po.id}`}
                      >
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Acknowledge
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard icon={PackageOpen} title="Materials RFQs to you" count={d.rfqs?.length || 0} testId="contractor-rfqs-card">
            {(d.rfqs?.length || 0) === 0 ? (
              <EmptyNote testId="contractor-rfqs-empty">No quote requests at the moment.</EmptyNote>
            ) : (
              <ul className="divide-y divide-border">
                {d.rfqs.map((r) => (
                  <li key={r.id} className="py-2.5" data-testid={`contractor-rfq-${r.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{r.rfq_number}</p>
                        <p className="text-xs text-muted-foreground">
                          {r.job_name} · {r.item_count} item{r.item_count === 1 ? "" : "s"}
                        </p>
                      </div>
                      <p className="shrink-0 text-xs font-semibold text-primary">
                        {r.due_date ? `Due ${fmtDate(r.due_date)}` : fmtDate(r.sent_at)}
                      </p>
                    </div>
                    {r.acknowledged ? (
                      <p className="mt-1 flex items-center gap-1 text-xs text-emerald-400" data-testid={`contractor-rfq-acked-${r.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Acknowledged
                      </p>
                    ) : viewOnly ? (
                      <p className="mt-1 text-xs text-muted-foreground" data-testid={`contractor-rfq-ack-locked-${r.id}`}>Awaiting acknowledgement · view only</p>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-1.5 h-7 border-primary/40 px-2.5 text-xs text-primary hover:bg-primary/10"
                        onClick={() => ackRfq(r)}
                        data-testid={`contractor-rfq-ack-btn-${r.id}`}
                      >
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Acknowledge
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      ) : null}

      {/* RFIs on my jobs */}
      <SectionCard icon={FileQuestion} title="RFIs on your jobs" count={d.rfis?.length || 0} testId="contractor-rfis-card">
        {(d.rfis?.length || 0) === 0 ? (
          <EmptyNote testId="contractor-rfis-empty">No RFIs on your jobs.</EmptyNote>
        ) : (
          <ul className="divide-y divide-border">
            {d.rfis.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`contractor-rfi-${r.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {r.number}
                    {r.subject ? ` — ${r.subject}` : ""}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {r.job_name} · {r.consultant_names?.length ? r.consultant_names.join(", ") : r.consultant_name}
                    {r.consultant_role && !(r.consultant_names?.length > 1) ? ` (${r.consultant_role})` : ""}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={
                    r.status === "open"
                      ? "shrink-0 border-amber-500/40 bg-amber-500/10 text-amber-400"
                      : "shrink-0 border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                  }
                >
                  {r.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* My jobs */}
      <SectionCard icon={FolderKanban} title="My jobs" count={d.jobs?.length || 0} testId="contractor-jobs-card">
        {(d.jobs?.length || 0) === 0 ? (
          <EmptyNote testId="contractor-jobs-empty">
            You haven't been assigned to any jobs yet — the builder will add you when you're needed.
          </EmptyNote>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {d.jobs.map((j) => (
              <Link
                key={j.id}
                to={`/jobs/${j.id}`}
                className="group rounded-xl border border-border bg-background p-3.5 transition-colors hover:border-primary/50"
                data-testid={`contractor-job-card-${j.id}`}
              >
                <p className="truncate text-sm font-semibold group-hover:text-primary">{j.name}</p>
                {j.address ? <p className="mt-0.5 truncate text-xs text-muted-foreground">{j.address}</p> : null}
                <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-primary">
                  View job &amp; schedule
                </p>
              </Link>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Issues I raised */}
      {(d.issues_raised?.length || 0) > 0 && (
        <SectionCard icon={ScrollText} title="Issues you've raised" count={d.issues_raised.length} testId="contractor-raised-card">
          <ul className="divide-y divide-border">
            {d.issues_raised.map((issue) => (
              <li key={issue.id} className="py-2.5" data-testid={`contractor-raised-${issue.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{issue.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {issue.job_name} · {fmtDate(issue.created_at)}
                    </p>
                  </div>
                  <StatusBadge status={issue.status} />
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {/* Report an issue dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="contractor-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Hammer className="h-5 w-5 text-primary" /> Report an issue to the builder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-[0.16em] text-primary">Job</p>
              {(d.jobs || []).length === 0 ? (
                <div className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground" data-testid="contractor-report-no-jobs">
                  You're not assigned to any jobs yet. Ask the builder to add you to a job — then you can report issues against it.
                </div>
              ) : (
                <Select value={reportJob} onValueChange={setReportJob}>
                  <SelectTrigger data-testid="contractor-report-job-select">
                    <SelectValue placeholder="Pick a job" />
                  </SelectTrigger>
                  <SelectContent>
                    {(d.jobs || []).map((j) => (
                      <SelectItem key={j.id} value={j.id} data-testid={`contractor-report-job-${j.id}`}>
                        {j.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <IssueItemsEditor
              items={reportItems}
              setItems={setReportItems}
              itemNoun="item"
              placeholder="What do you need the builder to look at?"
              testPrefix="contractor-report"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} data-testid="contractor-report-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitReport} disabled={submitting || (d.jobs || []).length === 0} data-testid="contractor-report-submit-btn">
              {submitting ? "Sending…" : "Send to builder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
